import { Alert, Button, Dropdown, Empty, Modal, Space, Table, Typography } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { MdMoreVert } from 'react-icons/md'

import { useNotification } from '@/Core/Hooks/useNotification'
import { Hint } from '@/Ship/Components/Hint'
import { RichTextView } from '@/Ship/Components/RichText/RichTextView'
import { StatusTag } from '@/Ship/Components/StatusTag'
import { formatDateTimeUk } from '@/Ship/Helpers/fieldViews'

import { LETTERS_PAGE_SIZE, LETTER_LABELS as L, LETTER_OWNERS, LETTER_TEXTS as T } from '../Contracts/ILetter'
import { LETTER_STATUS_MAP, isLetterStatus } from '../Contracts/LetterStatus'
import { normalizeAddresses } from '../Helpers/letterAddresses'
import { canEditLetter } from '../Helpers/letterRights'
import { LetterDrawerStore } from '../Stores/LetterDrawerStore'
import { OwnerLettersStore } from '../Stores/OwnerLettersStore'
import { LetterDrawer } from './LetterDrawer'
import { SendLetterButton } from './SendLetterButton'
import './letters.css'

import type { ILetter, LetterOwnerField } from '../Contracts/ILetter'
import type { MenuProps, TableColumnsType } from 'antd'

const { Text } = Typography

const dash = <Text type="secondary">—</Text>

const dateCell = (value: unknown) => (typeof value === 'string' && value !== '' ? formatDateTimeUk(value) : dash)

/** Колонки легасі (`CompanyEmailCrud.jsx:76-84`) + «Статус» (Q13.10). */
const COLUMNS: TableColumnsType<ILetter> = [
    { title: L.sentAt, key: 'sentAt', width: 150, render: (_: unknown, row) => dateCell(row.sentAt) },
    {
        title: L.scheduledAt,
        key: 'scheduledAt',
        width: 150,
        render: (_: unknown, row) => dateCell(row.scheduledAt),
    },
    {
        title: L.toWhom,
        key: 'toWhom',
        render: (_: unknown, row) => {
            const list = normalizeAddresses(row.toWhom)

            return list.length > 0 ? list.join(', ') : dash
        },
    },
    {
        title: L.subject,
        key: 'subject',
        render: (_: unknown, row) => (typeof row.subject === 'string' && row.subject !== '' ? row.subject : dash),
    },
    {
        // Відправлений — те, що пішло (тіло й підпис), решта — тіло. HTML —
        // лише через `RichTextView` (Л-01: легасі вставляв сирий HTML)
        title: L.body,
        key: 'body',
        width: 600,
        render: (_: unknown, row) => (
            <div className="letter-body-cell">
                <RichTextView html={row.status === 'sent' && typeof row.sentBody === 'string' ? row.sentBody : (row.body ?? null)} />
            </div>
        ),
    },
    {
        title: L.status,
        key: 'status',
        render: (_: unknown, row) =>
            isLetterStatus(row.status) ? (
                <StatusTag label={LETTER_STATUS_MAP[row.status].label} color={LETTER_STATUS_MAP[row.status].color} />
            ) : (
                dash
            ),
    },
]

export interface LettersTableProps {
    /** Чия вкладка: поле власника в листі — `client`, `carrier`, `clientContact`, `carrierContact` */
    ownerField: LetterOwnerField
    ownerUuid: string
}

/**
 * Вкладка «Листи» картки власника (`SPEC-013` §9.2; легасі `CompanyEmailCrud.jsx`):
 * «Відправити лист», листи власника зі статусами «Відкладено», «Відправлено»,
 * «Не відправлено», новіші першими, по 20 на сторінку. Фільтрів немає.
 *
 *  - **рядок** відкриває лист: невідправлений, який можна змінити
 *    (`_permissions.update`), — у редагуванні, решту — у перегляді;
 *  - **меню рядка** — власне (ядрове «⋮» зламане, HANDOFF): «Переглянути»,
 *    «Редагувати» за `_permissions.update`, «Видалити» за
 *    `_permissions.delete` з підтвердженням тексту ядра;
 *  - ядрового «Додати» немає (листи створює лише генерація, R-01) — на його
 *    місці «Відправити лист».
 *
 * Список перечитується, коли закривають шухляду листа: його могли
 * відправити, відкласти чи він міг стати «Не відправлено».
 */
export const LettersTable = observer(({ ownerField, ownerUuid }: LettersTableProps) => {
    const [store] = useState(() => new OwnerLettersStore(ownerField, LETTERS_PAGE_SIZE))
    const [drawer] = useState(() => new LetterDrawerStore())
    const [modal, modalHolder] = Modal.useModal()
    const { showError } = useNotification()

    useEffect(() => {
        store.bind(ownerUuid)
    }, [store, ownerUuid])

    const open = (row: ILetter, mode: 'view' | 'edit'): void => {
        if (typeof row.uuid === 'string') void drawer.open(row.uuid, mode, store.reload)
    }

    const confirmRemove = (row: ILetter): void => {
        const uuid = row.uuid

        if (typeof uuid !== 'string') return

        void modal.confirm({
            title: T.remove,
            content: T.removeConfirm,
            okText: T.yes,
            cancelText: T.no,
            okButtonProps: { danger: true },
            onOk: async () => {
                const error = await store.remove(uuid)

                if (error !== null) showError(T.error, error)
            },
        })
    }

    const menuOf = (row: ILetter): NonNullable<MenuProps['items']> => [
        { key: 'view', label: T.view, onClick: () => open(row, 'view') },
        ...(canEditLetter(row) ? [{ key: 'edit', label: T.edit, onClick: () => open(row, 'edit') }] : []),
        ...(row._permissions?.delete === true
            ? [{ key: 'delete', label: T.remove, danger: true, onClick: () => confirmRemove(row) }]
            : []),
    ]

    const columns: TableColumnsType<ILetter> = [
        ...COLUMNS,
        {
            title: '',
            key: 'actions',
            width: 56,
            render: (_: unknown, row: ILetter) => (
                // Клік у колонці дій не відкриває лист
                <span onClick={(event) => event.stopPropagation()}>
                    <Dropdown trigger={['click']} placement="bottomRight" menu={{ items: menuOf(row) }}>
                        <Button
                            type="text"
                            icon={<MdMoreVert />}
                            aria-label={T.actions}
                            loading={store.removingUuid === row.uuid}
                        />
                    </Dropdown>
                </span>
            ),
        },
    ]

    const provider = LETTER_OWNERS[ownerField].provider

    return (
        <Space orientation="vertical" style={{ width: '100%' }} size="middle">
            <Hint id="crm.letters.mailbox">
                Лист відправляється з робочої пошти Google Workspace автора листа, а підпис з його ім'ям, посадою й
                телефонами додається під час відправки. Без пароля додатку в картці автора лист не відправиться.
            </Hint>

            <SendLetterButton provider={provider} ownerUuid={ownerUuid} drawer={drawer} onChanged={store.reload} />

            {store.failed ? <Alert type="error" showIcon title={T.loadFailed} /> : null}

            {!store.isLoading && store.items.length === 0 && !store.failed ? (
                <Empty description={T.empty} />
            ) : (
                <Table<ILetter>
                    size="small"
                    rowKey={(row) => String(row.uuid)}
                    columns={columns}
                    dataSource={store.items}
                    loading={store.isLoading}
                    scroll={{ x: 'max-content' }}
                    pagination={
                        store.total > store.pageSize
                            ? {
                                  current: store.page,
                                  pageSize: store.pageSize,
                                  total: store.total,
                                  showSizeChanger: false,
                                  onChange: (page) => store.setPage(page),
                              }
                            : false
                    }
                    onRow={(row) => ({
                        style: { cursor: 'pointer' },
                        onClick: () => open(row, canEditLetter(row) ? 'edit' : 'view'),
                    })}
                />
            )}

            <LetterDrawer store={drawer} />
            {modalHolder}
        </Space>
    )
})
